import React, { useContext, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import styled from "styled-components";
import { Alert, Button, Menu, MenuItem, Spinner } from "@blueprintjs/core";
import { faGithub } from "@fortawesome/free-brands-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { AppToaster } from "@components/AppToaster";
import { TagSelector } from "@components/TagSelector";
import {
  useBasePath,
  useDeleteRepoMutation,
  useDeploymentTags,
  useRepos,
  useUpdateRepoMutation,
} from "@hooks";
import { OrgContext } from "@providers";
import { RepoChanges } from "@types";
import { Feature, withToaster } from "@utils";

import { PolicyWithRules } from "@pages/policy/types";

import { IgnoredPathsEditor } from "../IgnoredPathsEditor";
import { MoreButtonPopover } from "../MoreButtonPopover";
import { PolicySelector } from "../PolicySelector";
import { ItemHeading, ItemSubheading, SettingSwitch } from "../SettingSwitch";

import "./ProjectPanel.scss";

const SpacedSpan = styled.span`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 12px;
  }
`;

const ProjectHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-bottom: 1em;
`;

const HeaderLink = styled(Link)`
  color: inherit;
  :hover {
    color: var(--link-color);
    text-decoration: none;
  }
`;

type Props = {
  deploymentId?: string;
};

export const ProjectsPanel: React.FC<Props> = ({ deploymentId }) => {
  const { projectName } = useParams<any>();
  const { org } = useContext(OrgContext);
  const { data: repos, refetch: refresh } = useRepos();
  const history = useHistory();
  const basePath = useBasePath();
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteWarning, setShowDeleteWarning] = useState<boolean>(false);

  const { data: tags } = useDeploymentTags();

  const repo = repos?.find((r) => r.name === projectName);

  const { mutateAsync: deleteRepoMutation } = useDeleteRepoMutation();

  const deleteRepo = useMemo(
    () => async () => {
      const name = repo?.name;
      if (deploymentId === undefined || name === undefined) {
        return;
      }
      setIsSaving(true);
      await withToaster(
        () => deleteRepoMutation({ deploymentId, repoPath: name }),
        (err) => `Error deleting '${name}': ${err.body?.error}`
      )();
      setIsSaving(false);
      refresh();
      history.push(`${basePath}/projects`);
      AppToaster.show({
        message: `Removed project '${name}' from Semgrep`,
        intent: "success",
      });
    },
    [basePath, deploymentId, repo, refresh, history, deleteRepoMutation]
  );

  const openInGitHub = () => {
    const url = `https://github.com/${repo?.name}`;
    window.open(url, "_blank", "noopener");
  };

  const { mutateAsync: updateRepoMutation } = useUpdateRepoMutation();

  const onChange = async (change: RepoChanges) => {
    setIsSaving(true);

    updateRepoMutation({
      deploymentId: deploymentId!,
      repo: repo!,
      repoChanges: change,
    })
      .then((resp) => {
        if (resp.policies.length !== repo!.policies.length) {
          refresh();
        }
      })
      .catch((err) => `Could not update project: ${err.body?.error}`)
      .finally(() => setIsSaving(false));
  };

  const onPoliciesChange = (newPolicies: PolicyWithRules[]) => {
    onChange({ policies: newPolicies });
  };

  const onIgnoredPathsChange = (newPaths: string[]) => {
    onChange({ ignored_files: newPaths });
  };

  const onAutofixChange = (newAutofix: boolean) => {
    onChange({ autofix: newAutofix });
  };

  const onScaParityRulesChange = (newScaParityRules: boolean) => {
    onChange({ sca_parity_rules: newScaParityRules });
  };

  const onScaProvisionedChange = (newScaProvisioned: boolean) => {
    onChange({ sca_provisioned: newScaProvisioned });
  };

  const openInGithub = repo?.provider === "GitHub" && (
    <MenuItem
      text={"Open in GitHub"}
      icon={<FontAwesomeIcon icon={faGithub} />}
      onClick={openInGitHub}
    />
  );

  return (
    <div className="panel">
      <div className="panel-main">
        {repo === undefined ? (
          <div className="loading-container">
            <div className="loading-text">Undefined project</div>
          </div>
        ) : (
          <div>
            <ProjectHeader>
              <SpacedSpan>
                <span style={{ fontSize: "var(--global-font-size-m)" }}>
                  <HeaderLink to={`${basePath}/projects`}>Projects</HeaderLink>
                  {" / "}
                  <span style={{ color: "var(--global-purple-highlight)" }}>
                    {repo?.name}
                  </span>
                </span>
              </SpacedSpan>
              <SpacedSpan>
                {isSaving && (
                  <Spinner intent="primary" size={24} tagName={"span"} />
                )}
                <Button
                  intent="primary"
                  onClick={() => {
                    if (repo.latest_scan_at) {
                      history.push(`${basePath}/findings?repo=${repo.name}`);
                    } else {
                      history.push(
                        `${basePath}/projects/${repo.name?.split("/")[1]}/setup`
                      );
                    }
                  }}
                >
                  {repo.latest_scan_at ? "See findings" : "Retry setup"}
                </Button>
                <MoreButtonPopover
                  content={
                    <Menu>
                      {openInGithub}
                      <MenuItem
                        text={"Delete project"}
                        icon={"cross"}
                        onClick={() => setShowDeleteWarning(true)}
                      />
                    </Menu>
                  }
                />
              </SpacedSpan>
            </ProjectHeader>
            {repo.latest_scan_at === null ? (
              <div>
                Semgrep App has not received a completed scan from this project.
              </div>
            ) : (
              <div>
                <SettingSwitch
                  title={"Autofix"}
                  description={
                    org?.has_autofix
                      ? "Autofix is enabled for your entire organization."
                      : "Include autofix suggestions in pull request comments."
                  }
                  warning={
                    org?.has_autofix
                      ? "To turn it off, you must first disable it org-wide from the Settings panel."
                      : "Your autofixed code will be sent to Semgrep, but it will not be stored."
                  }
                  value={org?.has_autofix || repo.autofix}
                  onChange={() => onAutofixChange(!repo.autofix)}
                  disabled={org?.has_autofix}
                />
                {Feature.hasSCA({ org }) && (
                  <SettingSwitch
                    title={"SCA"}
                    description={"Run SCA rules with your CI scans"}
                    beta
                    value={!!repo.sca_provisioned}
                    onChange={() =>
                      onScaProvisionedChange(!repo.sca_provisioned)
                    }
                  />
                )}
                {Feature.hasSCA({ org }) && (
                  <SettingSwitch
                    title={"SCA historical coverage"}
                    description={
                      "Run auto-generated dependency-only rules in your SCA scans"
                    }
                    warning={
                      "While this will increase your coverage, these rules do not include reachability analysis, so expect a *lot* of false positives"
                    }
                    beta
                    value={!!repo.sca_settings.parity_rules}
                    onChange={() =>
                      onScaParityRulesChange(!repo.sca_settings.parity_rules)
                    }
                  />
                )}
                {Feature.isNolicy({ org }) ? null : (
                  <div>
                    <ItemHeading>Policies</ItemHeading>
                    <ItemSubheading>
                      The set of policies to run when scanning this project. If
                      empty, the{" "}
                      <Link to={`${basePath}/policies`}>default policies</Link>{" "}
                      are used.
                    </ItemSubheading>
                    <PolicySelector
                      selectedPolicies={
                        (repo.policies || []) as PolicyWithRules[]
                      }
                      setSelectedPolicies={onPoliciesChange}
                    />
                  </div>
                )}
                <div>
                  <ItemHeading>Path Ignores</ItemHeading>
                  <ItemSubheading>
                    The set of file paths for Semgrep to ignore in addition to
                    those specified by a local{" "}
                    <a href="https://semgrep.dev/docs/semgrep-ci/overview/#ignoring-files">
                      .semgrepignore
                    </a>{" "}
                    file.
                  </ItemSubheading>
                  <IgnoredPathsEditor
                    ignoredPaths={repo.ignored_files}
                    setIgnoredPaths={onIgnoredPathsChange}
                  />
                </div>

                {org && repo && tags && (
                  <div>
                    <ItemHeading>Tags</ItemHeading>
                    <TagSelector
                      tags={tags || []}
                      selectedTags={repo.tags || []}
                      repo={repo}
                    />
                  </div>
                )}
              </div>
            )}
            <br />
          </div>
        )}
        <Alert
          className="removeRepoWarning"
          style={{ width: "400px" }}
          icon="trash"
          intent="danger"
          isOpen={showDeleteWarning}
          confirmButtonText="Remove"
          cancelButtonText="Cancel"
          onConfirm={deleteRepo}
          onCancel={() => setShowDeleteWarning(false)}
          loading={isSaving}
        >
          <p>
            Are you sure you want to delete <b>{repo?.name}</b>? This will
            irreversibly delete all data we have about your repository and its
            prior Semgrep findings. In order to prevent Semgrep from running on
            this repository in the <em>future</em>, you will need to delete the
            semgrep.yml file from your GitHub configurations folder as well.
          </p>
        </Alert>
      </div>
    </div>
  );
};
